import gaiupApolloClient from "@/graphql/services/ApolloClient";
import {
  UpsertProjectCalculationDocument,
  UpsertProjectCalculationWithVersionDocument,
  UpsertProjectLocationDocument,
  UpsertProjectLocationWithVersionDocument,
  UpsertProjectSpecialConditionConfigDocument,
  UpsertProjectSpecialConditionConfigWithVersionDocument,
  UpsertProjectSpeckleParameterConfigDocument,
  UpsertProjectSpeckleParameterConfigWithVersionDocument,
  type UpsertProjectCalculationMutation,
  type UpsertProjectCalculationMutationVariables,
  type UpsertProjectCalculationWithVersionMutation,
  type UpsertProjectCalculationWithVersionMutationVariables,
  type UpsertProjectLocationMutation,
  type UpsertProjectLocationMutationVariables,
  type UpsertProjectLocationWithVersionMutation,
  type UpsertProjectLocationWithVersionMutationVariables,
  type UpsertProjectSpecialConditionConfigMutation,
  type UpsertProjectSpecialConditionConfigMutationVariables,
  type UpsertProjectSpecialConditionConfigWithVersionMutation,
  type UpsertProjectSpecialConditionConfigWithVersionMutationVariables,
  type UpsertProjectSpeckleParameterConfigMutation,
  type UpsertProjectSpeckleParameterConfigMutationVariables,
  type UpsertProjectSpeckleParameterConfigWithVersionMutation,
  type UpsertProjectSpeckleParameterConfigWithVersionMutationVariables,
} from "@/graphql";
import {
  isVersionEntityTypeUnsupported,
  isVersioningSchemaMissing,
} from "@/pages/ProjectVersionHistory/services/versioningSchema";

import {
  buildCalculationVersionInput,
  buildLocationVersionInput,
  buildSpecialConditionVersionInput,
  buildSpeckleParameterVersionInput,
  type LocationVersionMeta,
  type VersionMeta,
} from "./calculationVersionInput";
import type {
  ProjectCalculationInput,
  ProjectCalculationWriteResult,
  ProjectLocationWriteInput,
  ProjectSpecialConditionConfigInput,
  ProjectSpecialConditionWriteResult,
  ProjectSpeckleParameterConfigInput,
  ProjectSpeckleParameterWriteResult,
  ProjectWriteResult,
} from "../types/ProjectSetupWriteTypes";

import { ProjectWriteUnavailableError } from "./projectWriteErrors";

// Re-exported so existing importers keep working; the class itself moved to a module that does not
// pull in the Apollo client. See projectWriteErrors.ts.
export { ProjectWriteUnavailableError } from "./projectWriteErrors";

/**
 * Both writes share one shape: try the bundled document, and on the rollout signature retry without
 * the trail row rather than lose the write (rollout order is Hasura -> API -> UI).
 *
 * One wrinkle specific to this feature — isVersioningSchemaMissing matches
 * "not found in type: 'mutation_root'", which is also what the `user` role sees when the write
 * PERMISSIONS are missing. So if the plain document fails the same way, the environment cannot
 * write these tables at all, and that is a distinct state worth naming rather than a generic error.
 *
 * Exported for the rails that live in their own modules (projectReferenceAreaWrites.ts).
 */
export const withVersionFallback = async <TWithVersion, TPlain, TResult>(
  runWithVersion: () => Promise<TWithVersion | null | undefined>,
  runPlain: () => Promise<TPlain | null | undefined>,
  mapWithVersion: (data: TWithVersion) => TResult,
  mapPlain: (data: TPlain) => TResult
): Promise<TResult> => {
  try {
    const data = await runWithVersion();
    if (!data) throw new Error("The write returned nothing.");
    return mapWithVersion(data);
  } catch (mutationError) {
    if (!isVersioningSchemaMissing(mutationError) && !isVersionEntityTypeUnsupported(mutationError)) {
      throw mutationError;
    }
    try {
      const data = await runPlain();
      // The rollout error that sent us down the fallback is the useful context here, not this line.
      if (!data) throw new Error("The write returned nothing.", { cause: mutationError });
      return mapPlain(data);
    } catch (fallbackError) {
      // The same signature twice means it was never the trail that was missing.
      if (isVersioningSchemaMissing(fallbackError)) throw new ProjectWriteUnavailableError(fallbackError);
      throw fallbackError;
    }
  }
};

const calculationObject = (input: ProjectCalculationInput): UpsertProjectCalculationMutationVariables["calculation"] => ({
  projectId: input.projectId,
  calculationTypeId: input.calculationTypeId,
  considerationPeriod: input.considerationPeriod,
  thresholdLimit: input.thresholdLimit,
  installationThresholdLimit: input.installationThresholdLimit,
  useDefaultEmissions: input.useDefaultEmissions,
  // Nested insert: Hasura writes the parent first (or resolves the conflict onto the existing row)
  // and hangs these off whichever id came back. Verified to work on the conflict-update path, not
  // only on a fresh insert.
  stages: { data: input.stageIds.map((stageId) => ({ stageId })) },
});

export const upsertProjectCalculation = async (
  input: ProjectCalculationInput,
  meta: VersionMeta
): Promise<ProjectCalculationWriteResult> => {
  const calculation = calculationObject(input);

  const map = (
    data: UpsertProjectCalculationMutation | UpsertProjectCalculationWithVersionMutation,
    versionLogged: boolean
  ): ProjectCalculationWriteResult => {
    if (!data.calculation) throw new Error("The calculation row was not returned by Hasura.");
    return {
      rowId: data.calculation.id,
      revisionId: data.calculation.revisionId,
      deletedStages: data.deletedStages?.affected_rows ?? 0,
      stageIds: data.calculation.stages.map((stage) => stage.stageId),
      versionLogged,
    };
  };

  return withVersionFallback<
    UpsertProjectCalculationWithVersionMutation,
    UpsertProjectCalculationMutation,
    ProjectCalculationWriteResult
  >(
    async () =>
      (
        await gaiupApolloClient.mutate<
          UpsertProjectCalculationWithVersionMutation,
          UpsertProjectCalculationWithVersionMutationVariables
        >({
          mutation: UpsertProjectCalculationWithVersionDocument,
          variables: { projectId: input.projectId, calculation, version: buildCalculationVersionInput(input, meta) },
        })
      ).data,
    async () =>
      (
        await gaiupApolloClient.mutate<UpsertProjectCalculationMutation, UpsertProjectCalculationMutationVariables>({
          mutation: UpsertProjectCalculationDocument,
          variables: { projectId: input.projectId, calculation },
        })
      ).data,
    (data) => map(data, true),
    (data) => map(data, false)
  );
};

export const upsertProjectLocation = async (
  input: ProjectLocationWriteInput,
  meta: LocationVersionMeta
): Promise<ProjectWriteResult> => {
  const location: UpsertProjectLocationMutationVariables["location"] = {
    projectId: input.projectId,
    street: input.street,
    streetNumber: input.streetNumber,
    postalCode: input.postalCode,
    city: input.city,
    country: input.country,
  };

  const map = (
    data: UpsertProjectLocationMutation | UpsertProjectLocationWithVersionMutation,
    versionLogged: boolean
  ): ProjectWriteResult => {
    if (!data.location) throw new Error("The location row was not returned by Hasura.");
    return { rowId: data.location.id, revisionId: data.location.revisionId, versionLogged };
  };

  return withVersionFallback<UpsertProjectLocationWithVersionMutation, UpsertProjectLocationMutation, ProjectWriteResult>(
    async () =>
      (
        await gaiupApolloClient.mutate<
          UpsertProjectLocationWithVersionMutation,
          UpsertProjectLocationWithVersionMutationVariables
        >({
          mutation: UpsertProjectLocationWithVersionDocument,
          variables: { location, version: buildLocationVersionInput(input, meta) },
        })
      ).data,
    async () =>
      (
        await gaiupApolloClient.mutate<UpsertProjectLocationMutation, UpsertProjectLocationMutationVariables>({
          mutation: UpsertProjectLocationDocument,
          variables: { location },
        })
      ).data,
    (data) => map(data, true),
    (data) => map(data, false)
  );
};

/**
 * Whether this project uses special conditions.
 *
 * The fourth rail, and the one that finishes the job the other three started: it was the only field
 * in the REST base-info PUT that nothing else could write, so `updatebaseinfo` had to stay for it
 * alone. The project's scalars have their own rail, and the address and the Speckle configuration
 * ride in that payload without being editable anywhere after create.
 *
 * `meta` takes no `operation`: there is no "set" for a boolean that always exists — a project is
 * created with the flag off, so every write of it is a change of mind.
 */
export const upsertProjectSpecialConditionConfig = async (
  input: ProjectSpecialConditionConfigInput
): Promise<ProjectSpecialConditionWriteResult> => {
  const config: UpsertProjectSpecialConditionConfigMutationVariables["config"] = {
    projectId: input.projectId,
    enabled: input.enabled,
  };

  const map = (
    data: UpsertProjectSpecialConditionConfigMutation | UpsertProjectSpecialConditionConfigWithVersionMutation,
    versionLogged: boolean
  ): ProjectSpecialConditionWriteResult => {
    if (!data.config) throw new Error("The special condition config row was not returned by Hasura.");
    return { revisionId: data.config.revisionId, enabled: data.config.enabled, versionLogged };
  };

  return withVersionFallback<
    UpsertProjectSpecialConditionConfigWithVersionMutation,
    UpsertProjectSpecialConditionConfigMutation,
    ProjectSpecialConditionWriteResult
  >(
    async () =>
      (
        await gaiupApolloClient.mutate<
          UpsertProjectSpecialConditionConfigWithVersionMutation,
          UpsertProjectSpecialConditionConfigWithVersionMutationVariables
        >({
          mutation: UpsertProjectSpecialConditionConfigWithVersionDocument,
          variables: { config, version: buildSpecialConditionVersionInput(input) },
        })
      ).data,
    async () =>
      (
        await gaiupApolloClient.mutate<
          UpsertProjectSpecialConditionConfigMutation,
          UpsertProjectSpecialConditionConfigMutationVariables
        >({
          mutation: UpsertProjectSpecialConditionConfigDocument,
          variables: { config },
        })
      ).data,
    (data) => map(data, true),
    (data) => map(data, false)
  );
};

/**
 * The Speckle parameter mapping: which classification system the project uses and which model parameter
 * carries the code. Its own table, keyed on the project alone. The settings page edits it, and until this
 * rail it travelled only inside the REST base-info PUT, so a save that changed nothing else lost it.
 */
export const upsertProjectSpeckleParameterConfig = async (
  input: ProjectSpeckleParameterConfigInput,
  meta: Pick<VersionMeta, "entityLabel">
): Promise<ProjectSpeckleParameterWriteResult> => {
  const config: UpsertProjectSpeckleParameterConfigMutationVariables["config"] = {
    projectId: input.projectId,
    classificationTypeId: input.classificationTypeId,
    classificationCode: input.classificationCode,
  };

  const map = (
    data: UpsertProjectSpeckleParameterConfigMutation | UpsertProjectSpeckleParameterConfigWithVersionMutation,
    versionLogged: boolean
  ): ProjectSpeckleParameterWriteResult => {
    if (!data.config) throw new Error("The Speckle parameter config row was not returned by Hasura.");
    return {
      classificationTypeId: data.config.classificationTypeId ?? null,
      classificationCode: data.config.classificationCode,
      versionLogged,
    };
  };

  return withVersionFallback<
    UpsertProjectSpeckleParameterConfigWithVersionMutation,
    UpsertProjectSpeckleParameterConfigMutation,
    ProjectSpeckleParameterWriteResult
  >(
    async () =>
      (
        await gaiupApolloClient.mutate<
          UpsertProjectSpeckleParameterConfigWithVersionMutation,
          UpsertProjectSpeckleParameterConfigWithVersionMutationVariables
        >({
          mutation: UpsertProjectSpeckleParameterConfigWithVersionDocument,
          variables: { config, version: buildSpeckleParameterVersionInput(input, meta) },
        })
      ).data,
    async () =>
      (
        await gaiupApolloClient.mutate<
          UpsertProjectSpeckleParameterConfigMutation,
          UpsertProjectSpeckleParameterConfigMutationVariables
        >({
          mutation: UpsertProjectSpeckleParameterConfigDocument,
          variables: { config },
        })
      ).data,
    (data) => map(data, true),
    (data) => map(data, false)
  );
};
